// Admin: attach an existing exam (typically of type "practice_quiz") to a
// quiz-type lesson. Also lets the admin preview which quiz is currently
// linked and unlink it. Mirrors the visual language of AddDocumentDialog /
// AdminDocumentPreviewDialog so quiz rows in the curriculum feel consistent.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, HelpCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function AdminQuizLinkDialog({ open, onOpenChange, courseId, lesson, onSuccess }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!open || !courseId) return;
    setSelectedId(lesson?.linked_exam_id || "");
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/courses/${courseId}/available-quizzes`);
        setExams(data.data?.exams || []);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [open, courseId, lesson?.linked_exam_id]);

  const save = async () => {
    if (!lesson?.id) return;
    setSaving(true);
    try {
      await api.put(`/admin/lessons/${lesson.id}/link-quiz`, { exam_id: selectedId || null });
      toast.success(selectedId ? "Quiz linked to lesson." : "Quiz unlinked.");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  const currentLinked = exams.find((e) => e.id === lesson?.linked_exam_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" data-testid="admin-quiz-link-modal">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <HelpCircle className="h-4 w-4 text-[#101876]" />
            <span className="truncate">Link a quiz — {lesson?.lesson_title || "Quiz lesson"}</span>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="admin-quiz-link-close"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {currentLinked && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                <div className="flex-1">
                  <div className="font-bold text-emerald-900">Currently linked</div>
                  <div className="text-emerald-800">{currentLinked.exam_code} — {currentLinked.exam_title}</div>
                  <div className="mt-0.5 text-emerald-700/80">
                    {currentLinked.total_questions} questions · {currentLinked.duration_minutes} min · pass {currentLinked.passing_percentage}%
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">
              Select a quiz from the exam library
            </div>
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading available quizzes…
              </div>
            ) : exams.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500">
                No quizzes exist yet.{" "}
                <a href="/app/super-admin/exams/new" target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 font-semibold text-[#101876] underline">
                  Create one <ExternalLink className="h-3 w-3" />
                </a>{" "}first, then come back.
              </div>
            ) : (
              <div className="max-h-[45vh] space-y-1.5 overflow-y-auto rounded-md border border-slate-200 p-1.5">
                <label
                  className={`flex cursor-pointer items-center gap-2 rounded-md border p-2 text-xs transition ${selectedId === "" ? "border-rose-300 bg-rose-50" : "border-transparent hover:bg-slate-50"}`}
                  data-testid="admin-quiz-link-none"
                >
                  <input type="radio" name="exam" checked={selectedId === ""} onChange={() => setSelectedId("")} />
                  <span className="font-semibold text-slate-700">— None (unlink current) —</span>
                </label>
                {exams.map((e) => (
                  <label
                    key={e.id}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 text-xs transition ${selectedId === e.id ? "border-[#101876] bg-[#101876]/5" : "border-transparent hover:bg-slate-50"}`}
                    data-testid={`admin-quiz-link-opt-${e.id}`}
                  >
                    <input
                      type="radio"
                      name="exam"
                      checked={selectedId === e.id}
                      onChange={() => setSelectedId(e.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-bold text-slate-800">{e.exam_code} — {e.exam_title}</div>
                      {e.exam_description && (
                        <div className="mt-0.5 line-clamp-2 text-slate-500">{e.exam_description}</div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
                        <span>{e.total_questions ?? 0} Qs</span>
                        <span>·</span>
                        <span>{e.duration_minutes ?? 0} min</span>
                        <span>·</span>
                        <span>Pass {e.passing_percentage ?? 0}%</span>
                        <span>·</span>
                        <span className="rounded bg-slate-100 px-1 py-0.5 font-bold text-slate-600">{e.status}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/50 p-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="admin-quiz-link-cancel"
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="admin-quiz-link-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {selectedId ? "Save quiz link" : "Unlink"}
          </button>
        </div>
      </div>
    </div>
  );
}
